import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, Pencil, Trash2, Globe, MapPin, Phone, Lock, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { businessTypes } from '../mock';
import { toast } from '../hooks/use-toast';
import { trackEvent } from '../hooks/useAnalytics';

const blank = { name: '', type: 'other', address: '', phone: '', website: '' };

/**
 * Business Profile manager.
 *
 * Used by:
 *   • New users at /onboarding/business — required step before they can
 *     create ads (gate enforced at App-level on the Create / Dashboard
 *     routes by checking the count from /api/business-profiles).
 *   • Existing users at /settings/businesses — view, add up to N
 *     (per plan), edit, delete profiles.
 *
 * Plans: Starter/Pro = 1 profile, Enterprise = 5. Backend enforces this
 * with HTTP 402 — we surface the message inline.
 */
const BusinessProfilePage = ({ onboarding = false }) => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [limit, setLimit] = useState(1);
  const [used, setUsed] = useState(0);
  const [planName, setPlanName] = useState('Starter');
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/business-profiles');
      setProfiles(data.profiles || []);
      setLimit(data.limit ?? 1);
      setUsed(data.used ?? 0);
      // Plan name is best-effort — we just capitalise the plan id
      const p = (data.plan || 'starter');
      setPlanName(p.charAt(0).toUpperCase() + p.slice(1));
      // For onboarding flows: if user already has at least one profile,
      // they don't need to be on this page — bounce them to the dashboard.
      if (onboarding && (data.profiles || []).length > 0) {
        navigate('/dashboard', { replace: true });
      }
      // First-time onboarding → auto-open the form
      if (onboarding && (data.profiles || []).length === 0) {
        setShowForm(true);
      }
    } catch {
      toast({ title: 'Could not load businesses', description: 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const startCreate = () => {
    setDraft(blank);
    setEditingId(null);
    setShowForm(true);
  };

  const startEdit = (p) => {
    setDraft({
      name: p.name || '',
      type: p.type || 'other',
      address: p.address || '',
      phone: p.phone || '',
      website: p.website || '',
    });
    setEditingId(p.id);
    setShowForm(true);
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!draft.name.trim()) {
      toast({ title: 'Business name required' });
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/business-profiles/${editingId}`, draft);
        toast({ title: 'Business updated' });
      } else {
        await api.post('/business-profiles', draft);
        trackEvent('business_profile_created', { label: draft.type });
        toast({ title: 'Business added' });
      }
      setShowForm(false);
      setDraft(blank);
      setEditingId(null);
      await refresh();
      // First-time onboarding: bounce to dashboard once we have a profile
      const { data } = await api.get('/business-profiles');
      if (onboarding && (data.profiles || []).length > 0) {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Please try again.';
      toast({ title: 'Could not save', description: detail });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this business profile? This cannot be undone.')) return;
    try {
      await api.delete(`/business-profiles/${id}`);
      toast({ title: 'Deleted' });
      refresh();
    } catch {
      toast({ title: 'Could not delete' });
    }
  };

  const atLimit = used >= limit;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
              {onboarding ? 'Set up your business' : 'Your businesses'}
            </h1>
            <p className="mt-2 text-slate-600 max-w-2xl">
              {onboarding
                ? 'Tell us about your business so we can tailor every ad, image and video to your brand. You can edit this any time.'
                : `Your ${planName} plan allows ${limit} business profile${limit === 1 ? '' : 's'}. All ads, images and videos you generate are tied to the business you pick at creation.`}
            </p>
          </div>
          {!onboarding && (
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Used</div>
              <div className="text-2xl font-bold text-slate-900" data-testid="business-profile-usage">
                {used} <span className="text-slate-400 text-base font-medium">/ {limit}</span>
              </div>
            </div>
          )}
        </div>

        {atLimit && !onboarding && (
          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-900">
              You've used all {limit} business profile{limit === 1 ? '' : 's'} on your {planName} plan.
              {limit < 5 && (
                <> <button onClick={() => navigate('/choose-plan')} className="font-bold underline" data-testid="upgrade-from-limit">
                  Upgrade to Enterprise
                </button> for up to 5 businesses.</>
              )}
            </div>
          </div>
        )}

        <div className="mt-8 space-y-4">
          {!loading && profiles.length === 0 && !showForm && (
            <button
              onClick={startCreate}
              className="w-full rounded-2xl border-2 border-dashed border-slate-300 hover:border-slate-400 bg-white hover:bg-slate-50 p-12 text-center transition-colors"
              data-testid="business-profile-empty-create"
            >
              <Building2 className="w-10 h-10 mx-auto text-slate-400" />
              <p className="mt-3 font-semibold text-slate-700">Add your first business</p>
              <p className="mt-1 text-sm text-slate-500">Takes less than a minute.</p>
            </button>
          )}

          {profiles.map(p => (
            <div key={p.id} data-testid={`business-profile-row-${p.id}`} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-100 to-rose-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-orange-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-slate-900 truncate">{p.name}</div>
                <div className="mt-0.5 text-xs text-slate-500 capitalize">
                  {p.type || 'other'}
                  {p.address ? <> · <MapPin className="inline w-3 h-3 -mt-0.5" /> {p.address}</> : null}
                  {p.phone ? <> · <Phone className="inline w-3 h-3 -mt-0.5" /> {p.phone}</> : null}
                  {p.website ? <> · <Globe className="inline w-3 h-3 -mt-0.5" /> {p.website}</> : null}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => startEdit(p)} data-testid={`business-profile-edit-${p.id}`}>
                  <Pencil className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => remove(p.id)} className="text-rose-600 hover:text-rose-700" data-testid={`business-profile-delete-${p.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}

          {showForm && (
            <form onSubmit={save} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4" data-testid="business-profile-form">
              <h3 className="text-lg font-bold text-slate-900">
                {editingId ? 'Edit business' : 'Add business'}
              </h3>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Business name *</label>
                <Input value={draft.name} onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))} placeholder="e.g. Sunny Cafe" className="mt-1 h-11" data-testid="bp-name" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Business type</label>
                <Select value={draft.type} onValueChange={(v) => setDraft(d => ({ ...d, type: v }))}>
                  <SelectTrigger className="mt-1 h-11" data-testid="bp-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {businessTypes.map(b => <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Address</label>
                <Input value={draft.address} onChange={(e) => setDraft(d => ({ ...d, address: e.target.value }))} placeholder="Street, town, postcode" className="mt-1 h-11" data-testid="bp-address" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Phone</label>
                  <Input value={draft.phone} onChange={(e) => setDraft(d => ({ ...d, phone: e.target.value }))} placeholder="07712 345 678" className="mt-1 h-11" data-testid="bp-phone" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Website</label>
                  <Input value={draft.website} onChange={(e) => setDraft(d => ({ ...d, website: e.target.value }))} placeholder="www.yourbusiness.co.uk" className="mt-1 h-11" data-testid="bp-website" />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                {!onboarding && (
                  <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }} data-testid="bp-cancel">
                    Cancel
                  </Button>
                )}
                <Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="bp-save">
                  {saving ? 'Saving…' : (
                    <>
                      <Sparkles className="w-4 h-4 mr-1.5" />
                      {editingId ? 'Save changes' : (onboarding ? 'Continue' : 'Add business')}
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}

          {!showForm && profiles.length > 0 && !atLimit && (
            <button
              onClick={startCreate}
              className="w-full rounded-2xl border-2 border-dashed border-slate-300 hover:border-slate-400 bg-white hover:bg-slate-50 p-6 text-center transition-colors"
              data-testid="business-profile-add-another"
            >
              <Plus className="w-5 h-5 mx-auto text-slate-400" />
              <p className="mt-1 text-sm font-semibold text-slate-600">Add another business</p>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessProfilePage;
